import React from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface ConvexErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

const ConvexErrorFallback = ({ error, resetError }: { error?: Error; resetError: () => void }) => {
  // Check if this is a Convex authentication error
  const isAuthError = error?.message.includes('Invalid argument `id`') || 
                     error?.message.includes('Unable to decode ID') ||
                     error?.message.includes('CONVEX Q(users/get:me)') ||
                     error?.message.includes('Not authorized');

  // Check if this is a validation error
  const isValidationError = error?.message.includes('ReturnsValidationError') ||
                           error?.message.includes('Value does not match validator');

  if (isAuthError) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Authentication Error</AlertTitle>
        <AlertDescription>
          There was an issue with user authentication. Please try signing out and signing back in.
        </AlertDescription>
      </Alert>
    );
  }

  if (isValidationError) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Data Validation Error</AlertTitle>
        <AlertDescription>
          There was an issue with the data structure. This may be due to database schema changes. Please contact support if this persists.
        </AlertDescription>
      </Alert>
    );
  }

  // For other errors, show a generic message
  return (
    <Alert variant="destructive" className="m-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Something went wrong</AlertTitle>
      <AlertDescription>
        An unexpected error occurred. Please refresh the page or try again later.
      </AlertDescription>
    </Alert>
  );
};

export const ConvexErrorBoundary = ({ children, fallback }: ConvexErrorBoundaryProps) => {
  return (
    <ErrorBoundary fallback={fallback || ConvexErrorFallback}>
      {children}
    </ErrorBoundary>
  );
};